import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Play, Heart, Bookmark, Share2, Lock, Send, Youtube, Scissors, Film } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, StatusBadge, formatViews } from "../components/states";
import { Waveform } from "../components/Waveform";
import { toast } from "sonner";
import { BackButton, Breadcrumbs, ExternalLink, useEscape } from "../lib/nav";

function Highlights({ rec, user, time }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get(`/recordings/${rec.id}/highlights`).then((r) => setList(r.data)).catch(() => {}); }, [rec.id]);
  const clip = async () => {
    if (!user) { toast.error("Sign in to clip a highlight"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/recordings/${rec.id}/highlights`, { start: Math.max(0, time) }); toast.success("Rendering your 30s highlight…"); nav(`/h/${data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create highlight"); }
    setBusy(false);
  };
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  return (
    <div className="mt-6" data-testid="highlights-section">
      <div className="flex items-center justify-between mb-3"><h2 className="font-display text-xl font-bold uppercase flex items-center gap-2"><Film size={16} className="text-[#E8552A]" /> Highlights</h2>
        {rec.media_url && !rec.premium_locked && <button data-testid="clip-highlight" disabled={busy} onClick={clip} className="copper-btn rounded-full px-4 py-2 mono-label flex items-center gap-2 disabled:opacity-50"><Scissors size={14} /> CLIP 30s FROM {fmt(time)}</button>}
      </div>
      {list.length === 0 ? <p className="mono-label text-[#71717A]">NO HIGHLIGHTS YET — PLAY TO THE DROP AND HIT CLIP</p> : (
        <div className="flex gap-3 overflow-x-auto pb-2">{list.map((h) => <Link key={h.id} to={`/h/${h.id}`} data-testid={`highlight-${h.id}`} className="shrink-0 w-40 card-copper rounded-xl p-3 hover:border-[#E8552A]/60"><p className="mono-label text-[#E8552A]">{fmt(h.start)} · 30s</p><p className="text-xs text-[#A1A1AA] mt-1 truncate">by {h.user_name || "listener"}</p><p className="mono-label text-[#71717A] mt-1">{h.views} VIEWS</p></Link>)}</div>
      )}
    </div>
  );
}

export default function RecordingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [comments, setComments] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(0);
  const isMember = user && (user.is_member || ["DJ", "ADMIN", "SUPER_ADMIN"].includes(user.role));

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r, c] = await Promise.all([api.get(`/recordings/${id}`), api.get(`/recordings/${id}/comments`)]);
        setRec(r.data); setComments(c.data);
      } catch { toast.error("Recording not found"); }
      setLoading(false);
    })();
  }, [id]);

  const act = async (fn, okMsg) => {
    if (!user) { toast.error("Sign in first"); return; }
    try { await fn(); toast.success(okMsg); } catch { toast.error("Failed"); }
  };

  const sendComment = async () => {
    if (!msg.trim()) return;
    if (!isMember) { toast.error("Members only — become a member to comment"); return; }
    try { const { data } = await api.post(`/recordings/${id}/comments`, { body: msg }); setComments([data, ...comments]); setMsg(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Spinner />;
  if (!rec) return null;

  return (
    <div className="mx-auto max-w-[1300px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-4 flex-wrap mb-5"><BackButton fallback="/library" label="Back to archive" /><Breadcrumbs items={[{ label: "Live", to: "/" }, { label: "Archive", to: "/library" }, { label: rec.title }]} /></div>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div>
          {rec.media_url && !rec.premium_locked ? (
            <Waveform mediaUrl={rec.media_url} tracklist={rec.tracklist || []} locked={rec.tracklist_locked} onTime={setTime} />
          ) : (
            <div className="scanlines relative aspect-video rounded-xl overflow-hidden border border-[#27272A] bg-[#121216]">
              {rec.cover_image && <img src={rec.cover_image} alt={rec.title} className="w-full h-full object-cover opacity-50" />}
              <div className="absolute inset-0 flex items-center justify-center">
                {rec.external_url ? <a data-testid="play-recording-btn" href={rec.external_url} target="_blank" rel="noreferrer" className="w-20 h-20 rounded-full copper-btn flex items-center justify-center"><Play size={30} className="ml-1" /></a>
                  : <p className="mono-label text-[#A1A1AA]">{rec.premium_locked ? "PREMIUM MEMBERS ONLY" : "NO MEDIA AVAILABLE"}</p>}
              </div>
            </div>
          )}
          <h1 className="font-display text-3xl font-extrabold mt-5">{rec.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <span className="mono-label text-[#E8552A]">{rec.dj_name}</span>
            <span className="mono-label text-[#71717A]">{formatViews(rec.views)} views</span>
            {rec.youtube_url && <ExternalLink href={rec.youtube_url} className="mono-label text-[#71717A] hover:text-[#E8552A]"><Youtube size={14} /> YOUTUBE</ExternalLink>}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">{(rec.genres || []).map((g) => <span key={g} className="text-[0.65rem] font-mono uppercase text-[#A1A1AA] border border-[#27272A] rounded px-2 py-0.5">{g}</span>)}</div>
          <p className="text-sm text-[#A1A1AA] mt-4 leading-relaxed">{rec.description}</p>
          {rec.premium_locked && (
            <div data-testid="premium-locked" className="mt-4 card-copper rounded-xl p-4 flex items-center gap-3 border-[#E8552A]/40">
              <Lock className="text-[#E8552A]" size={20} />
              <div className="flex-1"><p className="font-display font-semibold">Premium-only set</p><p className="text-sm text-[#A1A1AA]">Playback is reserved for Premium members.</p></div>
              <Link to="/membership" className="copper-btn rounded-full px-4 py-2 mono-label">GO PREMIUM</Link>
            </div>
          )}
          <div className="flex gap-2 mt-5">
            <button data-testid="favorite-btn" onClick={() => act(() => api.post(`/user/favorite/${id}`), "Favorited")} className="flex items-center gap-2 border border-[#27272A] rounded-full px-4 py-2 mono-label hover:border-[#E8552A]"><Heart size={15} /> FAVORITE</button>
            <button data-testid="save-btn" onClick={() => act(() => api.post(`/user/save/${id}`), "Saved")} className="flex items-center gap-2 border border-[#27272A] rounded-full px-4 py-2 mono-label hover:border-[#E8552A]"><Bookmark size={15} /> SAVE</button>
            <button data-testid="share-btn" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} className="flex items-center gap-2 border border-[#27272A] rounded-full px-4 py-2 mono-label hover:border-[#E8552A]"><Share2 size={15} /> SHARE</button>
          </div>

          <Highlights rec={rec} user={user} time={time} />

          <div className="mt-8">
            <h2 className="font-display text-xl font-bold uppercase mb-4">Comments</h2>
            <div className="flex items-center gap-2 mb-4">
              <input data-testid="detail-comment-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()}
                placeholder={isMember ? "Add a comment…" : "Members only — become a member"} className="flex-1 bg-[#121216] border border-[#27272A] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8552A]" />
              <button data-testid="detail-send-comment" onClick={sendComment} className="copper-btn rounded-lg p-2.5"><Send size={16} /></button>
            </div>
            <div className="space-y-4">
              {comments.map((c) => (<div key={c.id}><span className="mono-label text-[#E8552A]">{c.handle}</span><p className="text-sm text-[#D4D4D8]">{c.body}</p></div>))}
            </div>
          </div>
        </div>

        <aside className="card-copper rounded-xl p-5 h-fit">
          <div className="flex items-center justify-between mb-4"><span className="font-display font-bold uppercase">Track ID</span><span className="mono-label text-[#71717A]">PRIME & ABOVE</span></div>
          {!rec.tracklist_locked && rec.tracklist?.length ? (
            <div className="space-y-4">
              {rec.tracklist.map((t, i) => (
                <div key={i} className="border-b border-[#27272A] pb-3 last:border-0">
                  <div className="flex items-center gap-2"><span className="mono-label text-[#E8552A]">{t.time}</span><span className="text-sm text-[#F4F4F5]">{t.title}</span></div>
                  <p className="mono-label text-[#71717A] mt-1">{t.artist} · {t.label}</p>
                  {t.buy && <a href={t.buy} target="_blank" rel="noreferrer" className="mono-label text-[#E8552A] hover:underline">BUY →</a>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Lock className="text-[#E8552A] mx-auto" size={24} />
              <p className="text-sm text-[#A1A1AA] mt-3">Upgrade to Prime to view the full tracklist.</p>
              <Link to="/membership" className="mono-label text-[#E8552A] mt-2 inline-block hover:underline">UNLOCK NOW →</Link>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
